import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaPhone, FaEnvelope, FaMapMarkerAlt, FaFileExcel, FaCreditCard } from 'react-icons/fa';
import * as XLSX from 'xlsx-js-style';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import useColleges from '../../../utils/useColleges';
import { useNavigate } from 'react-router-dom';
import './TotalDueList.css';

const TotalDueList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = isSuperAdmin || user?.role === 'admin' || (user?.colleges?.length > 0);
  const { colleges, loading: collegesLoading } = useColleges();

  const [defaulters, setDefaulters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterFeeName, setFilterFeeName] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMedium, setFilterMedium] = useState('');

  const loadDefaulters = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCollege) params.college = filterCollege;
      const res = await api.get('/payments/defaulters', { params });
      
      const data = (res.data.data || []).filter(d =>
        d.status === 'pending' || d.status === 'partial'
      );
      setDefaulters(data);
    } catch (err) {
      console.error(err);
      setDefaulters([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDefaulters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCollege]);

  useEffect(() => {
    if (!isAdmin && !isSuperAdmin && colleges.length > 0) setFilterCollege(String(colleges[0]._id));
    else if (isAdmin && !isSuperAdmin && colleges.length === 1) setFilterCollege(String(colleges[0]._id));
  }, [isAdmin, isSuperAdmin, colleges]);

  const uniqueSessions = useMemo(() => {
    const map = new Map();
    defaulters.forEach(d => {
      const s = d.session;
      if (s) map.set(s._id || s, s.name || s);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [defaulters]);

  const uniqueClasses = useMemo(() => {
    const map = new Map();
    defaulters.forEach(d => {
      const c = d.student?.class;
      if (c) {
        const id = c._id || c;
        const medium = c.medium ? ` (${c.medium})` : '';
        const label = (c.name || c) + medium;
        map.set(id, label);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [defaulters]);

  const uniqueBatches = useMemo(() => {
    const set = new Set();
    defaulters.forEach(d => {
      const b = d.student?.admissionYear;
      if (b) set.add(b);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [defaulters]);

  const uniqueMediums = useMemo(() => {
    const set = new Set();
    defaulters.forEach(d => { const m = d.student?.class?.medium || d.student?.medium; if (m) set.add(m); });
    return Array.from(set).sort();
  }, [defaulters]);

  const uniqueFeeNames = useMemo(() => {
    const set = new Set();
    defaulters.forEach(d => { if (d.feeName) set.add(d.feeName); });
    return Array.from(set).sort();
  }, [defaulters]);

  const filteredDefaulters = useMemo(() => {
    let list = defaulters;
    if (filterCollege) list = list.filter(d => String(d.college?._id || d.college) === filterCollege);
    if (filterSession) list = list.filter(d => String(d.session?._id || d.session) === filterSession);
    if (filterClass)   list = list.filter(d => String(d.student?.class?._id || d.student?.class) === filterClass);
    if (filterMedium)  list = list.filter(d => (d.student?.class?.medium || d.student?.medium || '') === filterMedium);
    if (filterBatch)   list = list.filter(d => String(d.student?.admissionYear) === filterBatch);
    if (filterFeeName) list = list.filter(d => d.feeName === filterFeeName);
    if (filterStatus)  list = list.filter(d => d.status === filterStatus);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(d =>
        (d.student?.name || '').toLowerCase().includes(q) ||
        (d.student?.fatherName || '').toLowerCase().includes(q) ||
        (d.student?.admissionNo || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [defaulters, filterCollege, filterSession, filterClass, filterMedium, filterBatch, filterFeeName, searchQuery, filterStatus]);

  // Filter defaulters first, then group by college
  const groupedDefaulters = useMemo(() => {
    const activeCollegeIds = colleges.map(c => String(c._id));
    
    // First filter the defaulters
    let filtered = defaulters.filter(d => {
      const collegeId = String(d.college?._id || d.college);
      // Only include if college is in active colleges list
      if (!activeCollegeIds.includes(collegeId)) return false;
      
      // Apply college filter
      if (filterCollege && collegeId !== filterCollege) return false;
      
      // Apply session filter
      if (filterSession && String(d.session?._id || d.session) !== filterSession) return false;
      
      // Apply class filter
      if (filterClass && String(d.student?.class?._id || d.student?.class) !== filterClass) return false;

      // Apply medium filter
      if (filterMedium && (d.student?.class?.medium || d.student?.medium || '') !== filterMedium) return false;
      
      // Apply batch filter
      if (filterBatch && String(d.student?.admissionYear) !== filterBatch) return false;
      
      // Apply fee name filter
      if (filterFeeName && d.feeName !== filterFeeName) return false;
      
      // Apply status filter
      if (filterStatus && d.status !== filterStatus) return false;
      
      // Apply search query
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchName = (d.student?.name || '').toLowerCase().includes(q);
        const matchFather = (d.student?.fatherName || '').toLowerCase().includes(q);
        const matchAdmNo = (d.student?.admissionNo || '').toLowerCase().includes(q);
        if (!matchName && !matchFather && !matchAdmNo) return false;
      }
      
      return true;
    });
    
    // Then group by college
    const g = {};
    filtered.forEach(d => {
      const key = d.college?.name || 'Unassigned';
      if (!g[key]) g[key] = [];
      g[key].push(d);
    });
    return g;
  }, [defaulters, colleges, filterCollege, filterSession, filterClass, filterMedium, filterBatch, filterFeeName, filterStatus, searchQuery]);

  const mergeByStudent = (list) => {
    const map = new Map();
    list.forEach(item => {
      const sid = String(item.student?._id || item.student);
      if (!map.has(sid)) {
        map.set(sid, {
          _studentId: sid,
          student: item.student,
          college: item.college,
          session: item.session,
          totalFee: 0, lateFee: 0, grandTotal: 0, deposit: 0, due: 0,
          fees: [],
          status: item.status,
        });
      }
      const s = map.get(sid);
      const fee = Number(item.amountDue || 0);
      const lf  = Number(item.lateFee   || 0);
      const dep = Number(item.amountPaid|| 0);
      const due = Number(item.balance   || 0);
      s.totalFee   += fee;
      s.lateFee    += lf;
      s.grandTotal += fee + lf;
      s.deposit    += dep;
      s.due        += due;
      if (s.status !== 'partial' && item.status === 'partial') s.status = 'partial';
      s.fees.push({ feeName: item.feeName, fee, lf, total: fee + lf, dep, due, status: item.status, _id: item._id });
    });
    return Array.from(map.values());
  };

  const getFilteredList = (list) => {
    // All filtering is now done in groupedDefaulters, just return the list
    return list;
  };

  const handlePayNow = (e, studentId, feeId = null) => {
    e.stopPropagation();
    const role = user?.role;
    const basePath = role === 'collegeadmin' ? '/collegeadmin' : role === 'clerk' ? '/clerk' : '/admin';
    const state = { preSelectStudentId: String(studentId) };
    if (feeId) state.preSelectFeeId = String(feeId);
    navigate(`${basePath}/fee-collection`, { state });
  };

  const handleExportExcel = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    const todayFormatted = `${dd}-${mm}-${yyyy}`;
    const headers = [
      'S.No', 'Student Name', 'Father Name', 'Batch', 'Session', 'Year',
      'Class', 'Fee Name', 'Fee (₹)', 'Late Fee (₹)', 'Total Fee (₹)',
      'Deposit (₹)', 'Due (₹)', 'Status', 'Phone', 'Email', 'Address'
    ];

    const activeCollege = colleges.find(c => c._id === filterCollege)?.name || 'All Colleges';
    const activeSession = uniqueSessions.find(s => s.id === filterSession)?.name || 'All';
    const activeClass = uniqueClasses.find(c => c.id === filterClass)?.name || 'All';
    
    const filtersText = `Filters Applied -> College: ${activeCollege} | Session: ${activeSession} | Class: ${activeClass} | Batch: ${filterBatch || 'All'} | Fee Name: ${filterFeeName || 'All'} | Search: ${searchQuery || 'None'}`;

    const colsCount = headers.length;
    const aoa = [
      ['TOTAL DUE LIST REPORT', ...Array(colsCount - 1).fill('')],
      [filtersText, ...Array(colsCount - 1).fill('')],
      Array(colsCount).fill('')
    ];

    let merges = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colsCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colsCount - 1 } }
    ];

    let currentRow = 3; 

    Object.entries(groupedDefaulters).forEach(([collegeName, list]) => {
      aoa.push([collegeName.toUpperCase(), ...Array(colsCount - 1).fill('')]);
      merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: colsCount - 1 } });
      currentRow++;

      aoa.push(headers);
      currentRow++;

      list.forEach((item, index) => {
        const student = item.student || {};
        const fee = Number(item.amountDue || 0);
        const lateFee = Number(item.lateFee || 0);
        const totalFee = fee + lateFee;
        const deposit = Number(item.amountPaid || 0);
        const due = Number(item.balance || 0);
        const cObj = student.class || {};
        const className = (cObj.name || student.class || '-') + (cObj.medium ? ` (${cObj.medium})` : '');

        aoa.push([
          index + 1,
          student.name || '-',
          student.fatherName || '-',
          student.admissionYear || '-',
          item.session?.name || '-',
          student.currentYear || '-',
          className,
          item.feeName || '-',
          fee,
          lateFee,
          totalFee,
          deposit,
          due,
          item.status === 'partial' ? 'Partial' : 'Pending',
          student.contact || '-',
          student.email || '-',
          student.address || '-'
        ]);
        currentRow++;
      });
      aoa.push(Array(colsCount).fill(''));
      currentRow++;
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = merges;
    ws['!cols'] = [
      { wch: 8 },  { wch: 25 }, { wch: 25 }, { wch: 12 }, { wch: 15 }, 
      { wch: 10 }, { wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 12 }, 
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 18 }, 
      { wch: 30 }, { wch: 45 }
    ];

    const border = {
      top: { style: 'thin', color: { auto: 1 } }, bottom: { style: 'thin', color: { auto: 1 } },
      left: { style: 'thin', color: { auto: 1 } }, right: { style: 'thin', color: { auto: 1 } }
    };

    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const ref = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[ref]) ws[ref] = { v: '', t: 's' };

        if (R === 0) {
          ws[ref].s = { font: { name: 'Arial', sz: 16, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1E3A5F' } }, alignment: { vertical: 'center', horizontal: 'center' } };
        } else if (R === 1) {
          ws[ref].s = { font: { name: 'Arial', sz: 10, italic: true, color: { rgb: '2C3E50' } }, fill: { fgColor: { rgb: 'F1F5F9' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (ws[ref].v && typeof ws[ref].v === 'string' && ws[ref].v === ws[ref].v.toUpperCase() && C === 0 && !headers.includes(ws[ref].v) && R > 2) {
           ws[ref].s = { font: { name: 'Arial', sz: 13, bold: true, color: { rgb: '1E3A5F' } }, fill: { fgColor: { rgb: 'E2E8F0' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (headers.includes(ws[ref].v)) {
           ws[ref].s = { font: { name: 'Arial', sz: 10, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '34495E' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (R > 2 && ws[ref].v !== '') {
           ws[ref].s = { font: { name: 'Arial', sz: 9 }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border };
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TotalDue');
    XLSX.writeFile(wb, `Total_Due_List_${todayFormatted}.xlsx`);
  };

  const mergedFiltered = useMemo(() => mergeByStudent(filteredDefaulters), [filteredDefaulters]);
  const totalDue = mergedFiltered.reduce((s, d) => s + d.due, 0);
  const totalFiltered = mergedFiltered.length;

  return (
    <div className="total-due-list-container">
      {/* Header */}
      <div className="total-due-list-header-row">
        <h2 className="total-due-list-title">Total Due List</h2>
        <div className="total-due-list-export-btns">
          <button className="total-due-list-btn-excel" onClick={handleExportExcel} disabled={filteredDefaulters.length === 0}>
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      {/* Summary strip */}
      {filteredDefaulters.length > 0 && (
        <div className="total-due-list-summary-strip">
          <span>Showing <strong>{totalFiltered}</strong> students with due</span>
          <span className="total-due-list-summary-due">Total Due: <strong>₹ {totalDue.toLocaleString()}</strong></span>
        </div>
      )}

      {/* Filters */}
      <div className="total-due-list-controls">
        <div className="total-due-list-search-wrapper">
          <FaSearch className="total-due-list-search-icon" />
          <input
            type="text"
            className="total-due-list-search-input"
            placeholder="Search by name, father's name, adm. no..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {(isAdmin || isSuperAdmin) && (
          <select className="total-due-list-select" value={filterCollege} onChange={e => setFilterCollege(e.target.value)}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <select className="total-due-list-select" value={filterSession} onChange={e => setFilterSession(e.target.value)}>
          <option value="">All Sessions</option>
          {uniqueSessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select className="total-due-list-select" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
          <option value="">All Classes</option>
          {uniqueClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <select className="total-due-list-select" value={filterMedium} onChange={e => setFilterMedium(e.target.value)}>
          <option value="">All Mediums</option>
          {uniqueMediums.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        <select className="total-due-list-select" value={filterBatch} onChange={e => setFilterBatch(e.target.value)}>
          <option value="">All Batches</option>
          {uniqueBatches.map(b => <option key={b} value={b}>{b}</option>)}
        </select>

        <select className="total-due-list-select" value={filterFeeName} onChange={e => setFilterFeeName(e.target.value)}>
          <option value="">All Fee Names</option>
          {uniqueFeeNames.map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        <select className="total-due-list-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All (Pending + Partial)</option>
          <option value="pending">Pending</option>
          <option value="partial">Partial</option>
        </select>

        {(filterSession || filterClass || filterMedium || filterBatch || filterFeeName || searchQuery || filterStatus) && (
          <button className="total-due-list-btn-clear" onClick={() => {
            setFilterSession(''); setFilterClass(''); setFilterMedium(''); setFilterBatch('');
            setFilterFeeName(''); setSearchQuery(''); setFilterStatus('');
          }}>✕ Clear</button>
        )}
      </div>

      {/* Table Area */}
      {loading ? (
        <div className="total-due-list-state-msg">Loading total due list...</div>
      ) : Object.keys(groupedDefaulters).length === 0 ? (
        <div className="total-due-list-state-msg total-due-list-empty">No due records found.</div>
      ) : (
        <div className="total-due-list-grouped-container">
          {Object.entries(groupedDefaulters).map(([collegeName, allList]) => {
            const defList = getFilteredList(allList);
            return (
            <div key={collegeName} className="total-due-list-table-group">
              <h3 className="total-due-list-group-header">🏫 {collegeName} <span className="total-due-list-group-count">{mergeByStudent(defList).length} students</span></h3>
              <div className="total-due-list-table-wrapper">
                <table className="total-due-list-table">
                  <thead>
                    <tr>
                      <th className="total-due-list-col-sno">S.No</th>
                      <th className="total-due-list-align-left total-due-list-col-name">Student Name</th>
                      <th className="total-due-list-col-fname">Father's Name</th>
                      <th className="total-due-list-col-batch">Batch</th>
                      <th className="total-due-list-col-session">Session</th>
                      <th className="total-due-list-col-year">Year</th>
                      <th className="total-due-list-col-class">Class</th>
                      <th className="total-due-list-col-totalfee">Total Fee (₹)</th>
                      <th className="total-due-list-col-deposit">Deposit (₹)</th>
                      <th className="total-due-list-col-due">Due (₹)</th>
                      <th className="total-due-list-col-status">Status</th>
                      {!isSuperAdmin && <th className="total-due-list-col-action">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {defList.length === 0 ? (
                      <tr>
                        <td colSpan={isSuperAdmin ? "11" : "12"} className="total-due-list-no-results">
                          <span className="total-due-list-no-results-icon">🔍</span>
                          No matching records found for current filters
                        </td>
                      </tr>
                    ) : mergeByStudent(defList).map((item, i) => {
                      const rowId   = item._studentId;
                      const student = item.student || {};
                      const classObj = student.class || {};
                      const className = classObj.name || student.class || '—';
                      const medium    = classObj.medium ? ` (${classObj.medium})` : (student.medium ? ` (${student.medium})` : '');

                      return (
                        <React.Fragment key={rowId}>
                          <tr className="total-due-list-main-row" onClick={() => setExpandedRow(expandedRow === rowId ? null : rowId)}>
                            <td className="total-due-list-col-sno">{i + 1}</td>
                            <td className="total-due-list-align-left total-due-list-col-name">
                              <div className="total-due-list-name-wrapper">
                                <div>
                                  <span style={{ fontWeight: 600 }}>{student.name || '—'}</span>
                                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{student.admissionNo || ''}</div>
                                </div>
                                <button className="total-due-list-expand-btn">
                                  {expandedRow === rowId ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                              </div>
                            </td>
                            <td className="total-due-list-col-fname">{student.fatherName || '—'}</td>
                            <td className="total-due-list-col-batch">
                              <span className="total-due-list-badge">{student.admissionYear || '—'}</span>
                            </td>
                            <td className="total-due-list-col-session">{item.session?.name || '—'}</td>
                            <td className="total-due-list-col-year">{student.currentYear || '—'}</td>
                            <td className="total-due-list-col-class">
                              <span style={{ fontWeight: 600 }}>{className}</span>
                              {medium && <div style={{ fontSize: '0.72rem', color: '#7c3aed' }}>{medium}</div>}
                            </td>
                            <td className="total-due-list-col-totalfee total-due-list-amt-totalfee">{item.grandTotal.toLocaleString()}</td>
                            <td className="total-due-list-col-deposit total-due-list-amt-paid">{item.deposit.toLocaleString()}</td>
                            <td className="total-due-list-col-due total-due-list-amt-due">{item.due.toLocaleString()}</td>
                            <td className="total-due-list-col-status">
                              <span className={`total-due-list-status-badge total-due-list-status-${item.status}`}>
                                {item.status === 'partial' ? 'Partial' : 'Pending'}
                              </span>
                            </td>
                            {!isSuperAdmin && (
                              <td className="total-due-list-col-action">
                                <button className="total-due-list-btn-pay" onClick={(e) => handlePayNow(e, item._studentId)}>
                                  <FaCreditCard /> Pay
                                </button>
                              </td>
                            )}
                          </tr>

                          {/* Expanded Row */}
                          {expandedRow === rowId && (
                            <tr className="total-due-list-expanded-row">
                              <td colSpan={isSuperAdmin ? "11" : "12"}>
                                <div className="total-due-list-expanded-content">
                                  {/* Student Info Grid */}
                                  <div className="total-due-list-expanded-grid">
                                    <div className="total-due-list-exp-item total-due-list-exp-fname">
                                      <span className="total-due-list-exp-label">Father's Name</span>
                                      <span className="total-due-list-exp-value">{student.fatherName || '—'}</span>
                                    </div>
                                    <div className="total-due-list-exp-item total-due-list-exp-batch">
                                      <span className="total-due-list-exp-label">Batch</span>
                                      <span className="total-due-list-exp-value">{student.admissionYear || '—'}</span>
                                    </div>
                                    <div className="total-due-list-exp-item total-due-list-exp-session">
                                      <span className="total-due-list-exp-label">Session</span>
                                      <span className="total-due-list-exp-value">{item.session?.name || '—'}</span>
                                    </div>
                                    <div className="total-due-list-exp-item total-due-list-exp-year">
                                      <span className="total-due-list-exp-label">Year</span>
                                      <span className="total-due-list-exp-value">{student.currentYear || '—'}</span>
                                    </div>
                                    <div className="total-due-list-exp-item total-due-list-exp-class">
                                      <span className="total-due-list-exp-label">Class</span>
                                      <span className="total-due-list-exp-value">{className}{medium}</span>
                                    </div>
                                    <div className="total-due-list-exp-item total-due-list-exp-totalfee">
                                      <span className="total-due-list-exp-label">Total Fee</span>
                                      <span className="total-due-list-exp-value total-due-list-amt-totalfee">₹ {item.grandTotal.toLocaleString()}</span>
                                    </div>
                                    <div className="total-due-list-exp-item total-due-list-exp-deposit">
                                      <span className="total-due-list-exp-label">Deposit</span>
                                      <span className="total-due-list-exp-value total-due-list-amt-paid">₹ {item.deposit.toLocaleString()}</span>
                                    </div>
                                    <div className="total-due-list-exp-item total-due-list-exp-due">
                                      <span className="total-due-list-exp-label">Due</span>
                                      <span className="total-due-list-exp-value total-due-list-amt-due">₹ {item.due.toLocaleString()}</span>
                                    </div>
                                    <div className="total-due-list-exp-item total-due-list-exp-status">
                                      <span className="total-due-list-exp-label">Status</span>
                                      <span className="total-due-list-exp-value">
                                        <span className={`total-due-list-status-badge total-due-list-status-${item.status}`}>
                                          {item.status === 'partial' ? 'Partial' : 'Pending'}
                                        </span>
                                      </span>
                                    </div>
                                    {!isSuperAdmin && (
                                      <div className="total-due-list-exp-item total-due-list-exp-action">
                                        <span className="total-due-list-exp-label">Action</span>
                                        <span className="total-due-list-exp-value">
                                          <button className="total-due-list-btn-pay" onClick={(e) => handlePayNow(e, item._studentId)}>
                                            <FaCreditCard /> Pay Now
                                          </button>
                                        </span>
                                      </div>
                                    )}
                                    <div className="total-due-list-exp-item">
                                      <span className="total-due-list-exp-label">Phone</span>
                                      <span className="total-due-list-exp-value"><FaPhone color="#94a3b8" style={{marginRight:4}}/>{student.contact || 'N/A'}</span>
                                    </div>
                                    <div className="total-due-list-exp-item">
                                      <span className="total-due-list-exp-label">Email</span>
                                      <span className="total-due-list-exp-value"><FaEnvelope color="#94a3b8" style={{marginRight:4}}/>{student.email || 'N/A'}</span>
                                    </div>
                                    <div className="total-due-list-exp-item total-due-list-exp-fullwidth">
                                      <span className="total-due-list-exp-label">Address</span>
                                      <span className="total-due-list-exp-value"><FaMapMarkerAlt color="#94a3b8" style={{marginRight:4}}/>{student.address || 'N/A'}</span>
                                    </div>
                                  </div>
                                  
                                  {/* Fee Breakdown Inner Table with Protected Horizontal Scroll */}
                                  <div className="total-due-list-inner-table-wrapper">
                                    <table className="total-due-list-inner-table">
                                      <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                          <th style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>Fee Name</th>
                                          <th style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>Fee (₹)</th>
                                          <th style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>Late Fee (₹)</th>
                                          <th style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>Total (₹)</th>
                                          <th style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>Deposit (₹)</th>
                                          <th style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>Due (₹)</th>
                                          <th style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>Status</th>
                                          {!isSuperAdmin && <th style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>Action</th>}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {item.fees.map((f, fi) => (
                                          <tr key={fi} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '6px 10px' }}><span className="total-due-list-feename-badge">{f.feeName || '—'}</span></td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }} className="total-due-list-amt-total">{f.fee.toLocaleString()}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }} className="total-due-list-amt-late">{f.lf.toLocaleString()}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }} className="total-due-list-amt-totalfee">{f.total.toLocaleString()}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }} className="total-due-list-amt-paid">{f.dep.toLocaleString()}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }} className="total-due-list-amt-due">{f.due.toLocaleString()}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                              <span className={`total-due-list-status-badge total-due-list-status-${f.status}`}>
                                                {f.status === 'partial' ? 'Partial' : 'Pending'}
                                              </span>
                                            </td>
                                            {!isSuperAdmin && (
                                              <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                                <button className="total-due-list-btn-pay" onClick={(e) => handlePayNow(e, item._studentId, f._id)}>
                                                  <FaCreditCard /> Pay
                                                </button>
                                              </td>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TotalDueList;